import { useState } from 'react';
import SEOHead from '@/components/feature/SEOHead';
import Breadcrumb from '@/components/feature/Breadcrumb';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { reviews, stats } from '@/mocks/reviews';

function BrandSection() {
  return (
    <section className="py-14 md:py-20 bg-background-50">
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <div className="flex flex-col lg:flex-row items-center gap-10 lg:gap-16">
          <div className="flex-1">
            <h1 className="text-2xl md:text-3xl lg:text-4xl font-heading font-bold text-foreground-950 mb-5">
              关于精品视频精选
            </h1>
            <div className="space-y-4 text-sm text-foreground-600 leading-relaxed">
              <p>
                精品视频精选平台创立于2024年，是一家专注于高清优质视频内容聚合与推荐的综合性视频门户。平台汇聚全网优质视频资源，涵盖影视、教育、娱乐、纪录片、科技、生活方式、音乐艺术、动漫等多个领域，致力于为每一位用户提供极致的视听体验。
              </p>
              <p>
                我们坚信，好的内容值得被更多人看到。在这个信息爆炸的时代，我们的内容团队每日对海量视频进行严格筛选与把关，确保每一部上线的作品都符合"画质精良、内容优质、价值导向正向"的三大核心标准。
              </p>
              <p>
                精品视频精选不仅是一个视频平台，更是一个连接优质内容创作者与广大观众的内容生态。我们与众多优质内容制作方建立了深度合作关系，持续为用户带来独家精品内容。
              </p>
              <p>
                <strong className="text-foreground-900">我们的使命：</strong>让每一个人都能轻松发现并享受高质量的视听内容。
              </p>
            </div>
          </div>
          <div className="flex-1">
            <img
              src="https://readdy.ai/api/search-image?query=Modern%20minimalist%20office%20space%20with%20creative%20team%20collaboration%2C%20warm%20natural%20lighting%2C%20professional%20media%20company%20atmosphere%2C%20clean%20design%20aesthetic%2C%20video%20production%20elements%2C%20friendly%20workspace&width=700&height=500&seq=about-brand&orientation=landscape"
              alt="精品视频精选团队"
              className="w-full rounded-lg"
            />
          </div>
        </div>
      </div>
    </section>
  );
}

function StatsAboutSection() {
  return (
    <section className="py-12 md:py-16 bg-primary-500">
      <div className="max-w-5xl mx-auto px-4 md:px-6">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
          {stats.map((stat) => (
            <div key={stat.label} className="text-center">
              <div className="text-3xl md:text-4xl font-heading font-bold text-white mb-1">
                {stat.value}
              </div>
              <div className="text-sm text-white/70">{stat.label}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function ReputationSection() {
  return (
    <section className="py-14 md:py-20 bg-background-100">
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <div className="text-center mb-12">
          <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-3">
            用户口碑与评价
          </h2>
          <p className="text-foreground-500 text-sm max-w-xl mx-auto">
            来自真实用户的使用反馈，是我们不断进步的动力
          </p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {reviews.map((review) => (
            <div
              key={review.id}
              className="bg-background-50 border border-background-200/70 rounded-lg p-5"
            >
              <div className="flex items-center gap-3 mb-4">
                <img
                  src={review.avatar}
                  alt={review.name}
                  className="w-11 h-11 rounded-full object-cover"
                  loading="lazy"
                />
                <div>
                  <div className="text-sm font-semibold text-foreground-900">{review.name}</div>
                  <div className="text-xs text-foreground-500">{review.role}</div>
                </div>
              </div>
              <div className="flex gap-0.5 mb-3">
                {Array.from({ length: review.rating }).map((_, i) => (
                  <i key={i} className="ri-star-fill text-accent-500 text-xs w-3.5 h-3.5 flex items-center justify-center"></i>
                ))}
              </div>
              <p className="text-xs text-foreground-600 leading-relaxed">{review.content}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function ContactSection() {
  const [formData, setFormData] = useState({ name: '', email: '', message: '' });
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [formMessage, setFormMessage] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const fd = new FormData(form);
    const honeypotValue = (fd.get('company_alt') as string || '').trim();
    if (honeypotValue) {
      setStatus('success');
      setFormMessage('您的留言已成功发送，我们会尽快回复！');
      return;
    }
    if (formData.message.length > 500) {
      setStatus('error');
      setFormMessage('留言内容不能超过500个字符。');
      return;
    }
    setStatus('loading');
    try {
      const res = await fetch('https://readdy.ai/api/form/d9rcqd6e3oq4jqumtvl0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams({
          name: formData.name,
          email: formData.email,
          message: formData.message,
        }).toString(),
      });
      const responseText = await res.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }
      if (res.ok && parsed?.code === 'OK') {
        setStatus('success');
        setFormMessage('您的留言已成功发送，我们会尽快回复！');
        setFormData({ name: '', email: '', message: '' });
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || '';
        setStatus('error');
        setFormMessage(serverMsg || '发送失败，请稍后重试。');
      }
    } catch {
      setStatus('error');
      setFormMessage('网络错误，请检查连接后重试。');
    }
  };

  return (
    <section className="py-14 md:py-20 bg-background-50">
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <div className="text-center mb-12">
          <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-3">
            联系我们
          </h2>
          <p className="text-foreground-500 text-sm max-w-xl mx-auto">
            有任何问题或建议？我们期待听到您的声音
          </p>
        </div>
        <div className="flex flex-col lg:flex-row gap-10 max-w-5xl mx-auto">
          <div className="flex-1 space-y-6">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 rounded-lg bg-primary-50 flex items-center justify-center flex-shrink-0">
                <i className="ri-map-pin-line text-primary-500 text-lg w-5 h-5 flex items-center justify-center"></i>
              </div>
              <div>
                <h4 className="text-sm font-semibold text-foreground-900 mb-1">公司地址</h4>
                <p className="text-xs text-foreground-500">北京市朝阳区科技园区88号创新大厦15层</p>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 rounded-lg bg-primary-50 flex items-center justify-center flex-shrink-0">
                <i className="ri-phone-line text-primary-500 text-lg w-5 h-5 flex items-center justify-center"></i>
              </div>
              <div>
                <h4 className="text-sm font-semibold text-foreground-900 mb-1">客服电话</h4>
                <p className="text-xs text-foreground-500">400-888-9999（工作日 9:00-18:00）</p>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 rounded-lg bg-primary-50 flex items-center justify-center flex-shrink-0">
                <i className="ri-mail-line text-primary-500 text-lg w-5 h-5 flex items-center justify-center"></i>
              </div>
              <div>
                <h4 className="text-sm font-semibold text-foreground-900 mb-1">电子邮箱</h4>
                <p className="text-xs text-foreground-500">contact@jingpinsp.com</p>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 rounded-lg bg-primary-50 flex items-center justify-center flex-shrink-0">
                <i className="ri-wechat-line text-primary-500 text-lg w-5 h-5 flex items-center justify-center"></i>
              </div>
              <div>
                <h4 className="text-sm font-semibold text-foreground-900 mb-1">微信公众号</h4>
                <p className="text-xs text-foreground-500">搜索"精品视频精选"关注我们</p>
              </div>
            </div>
          </div>

          <div className="flex-1">
            <form
              onSubmit={handleSubmit}
              data-readdy-form
              className="bg-background-100 border border-background-200/70 rounded-lg p-6 space-y-4"
            >
              <div className="ab-9873-field-wrap">
                <input type="text" name="company_alt" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly />
              </div>
              <div>
                <label htmlFor="contact-name" className="block text-xs font-medium text-foreground-700 mb-1.5">
                  姓名
                </label>
                <input
                  id="contact-name"
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  className="w-full px-4 py-2.5 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all"
                  placeholder="请输入您的姓名"
                />
              </div>
              <div>
                <label htmlFor="contact-email" className="block text-xs font-medium text-foreground-700 mb-1.5">
                  邮箱
                </label>
                <input
                  id="contact-email"
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                  className="w-full px-4 py-2.5 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all"
                  placeholder="请输入您的邮箱地址"
                />
              </div>
              <div>
                <label htmlFor="contact-message" className="block text-xs font-medium text-foreground-700 mb-1.5">
                  留言内容
                  <span className="text-foreground-400 ml-1">（限500字）</span>
                </label>
                <textarea
                  id="contact-message"
                  name="message"
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  required
                  maxLength={500}
                  rows={4}
                  className="w-full px-4 py-2.5 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all resize-none"
                  placeholder="请输入您想咨询的内容..."
                />
                <p className="text-xs text-foreground-400 mt-1">{formData.message.length}/500</p>
              </div>
              <button
                type="submit"
                disabled={status === 'loading'}
                className="w-full py-2.5 bg-primary-500 hover:bg-primary-600 text-white font-medium rounded-md transition-all duration-300 whitespace-nowrap text-sm disabled:opacity-60 cursor-pointer"
              >
                {status === 'loading' ? '发送中...' : '发送留言'}
              </button>
              {status === 'success' && (
                <p className="text-sm text-secondary-600 text-center">{formMessage}</p>
              )}
              {status === 'error' && (
                <p className="text-sm text-primary-500 text-center">{formMessage}</p>
              )}
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}

function ValuesSection() {
  const values = [
    {
      icon: 'ri-shield-check-line',
      title: '品质为先',
      desc: '每一部视频均经过严格筛选，确保内容精良、画质清晰',
    },
    {
      icon: 'ri-user-heart-line',
      title: '用户至上',
      desc: '以用户体验为核心，持续优化产品功能和内容推荐算法',
    },
    {
      icon: 'ri-lightbulb-line',
      title: '创新驱动',
      desc: '不断探索新技术、新模式，为用户带来更优质的视听体验',
    },
    {
      icon: 'ri-hand-heart-line',
      title: '责任担当',
      desc: '坚持正向价值导向，传播优质内容，承担平台社会责任',
    },
  ];

  return (
    <section className="py-14 md:py-20 bg-background-50">
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <div className="text-center mb-12">
          <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-3">
            核心价值观
          </h2>
          <p className="text-foreground-500 text-sm max-w-xl mx-auto">
            指导我们前行的信念与准则
          </p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {values.map((v) => (
            <div key={v.title} className="text-center p-6">
              <div className="w-14 h-14 rounded-full bg-accent-100 flex items-center justify-center mx-auto mb-4">
                <i className={`${v.icon} text-accent-500 text-2xl w-6 h-6 flex items-center justify-center`}></i>
              </div>
              <h3 className="text-sm font-semibold text-foreground-900 mb-2">{v.title}</h3>
              <p className="text-xs text-foreground-500 leading-relaxed">{v.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export default function AboutPage() {
  const schemaData = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: '关于我们 - 精品视频精选平台',
    description: '精品视频精选平台创立于2024年，是一家专注于高清优质视频内容聚合与推荐的综合性视频门户。了解我们的品牌故事、核心价值观与联系方式。',
    url: 'https://www.jingpinsp.com/about',
    isPartOf: {
      '@type': 'WebSite',
      name: '精品视频精选平台',
    },
    about: {
      '@type': 'Organization',
      name: '精品视频精选平台',
      description: '精品视频汇聚全网高清优质内容，涵盖影视、教育、娱乐、纪录片等多元类型。',
      foundingDate: '2024',
      contactPoint: {
        '@type': 'ContactPoint',
        telephone: '400-888-9999',
        contactType: 'customer service',
        availableLanguage: 'Chinese',
      },
      address: {
        '@type': 'PostalAddress',
        addressLocality: '北京',
        addressRegion: '朝阳区',
        streetAddress: '科技园区88号',
        addressCountry: 'CN',
      },
    },
  };

  return (
    <>
      <SEOHead
        title="关于我们 - 精品视频精选平台"
        description="了解精品视频精选平台的品牌故事、核心价值观、用户口碑与联系方式。精品视频汇聚全网高清优质内容，涵盖影视、教育、娱乐、纪录片等多元类型。"
        keywords="关于我们,品牌介绍,联系方式,精品视频"
        canonicalUrl="https://www.jingpinsp.com/about"
        ogTitle="关于我们 - 精品视频精选平台"
        ogDescription="了解精品视频精选平台的品牌故事、核心价值观与联系方式。"
        schemaData={schemaData}
      />
      <Navbar />
      <main className="pt-16">
        <div className="bg-background-100">
          <div className="max-w-7xl mx-auto px-4 md:px-6 pt-6">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: '关于我们' },
              ]}
            />
          </div>
          <div className="max-w-7xl mx-auto px-4 md:px-6 py-8 md:py-12 text-center">
            <h1 className="text-2xl md:text-3xl lg:text-4xl font-heading font-bold text-foreground-950">
              关于我们
            </h1>
            <p className="text-foreground-500 text-sm mt-3 max-w-xl mx-auto">
              了解精品视频精选平台的品牌故事与核心价值
            </p>
          </div>
        </div>

        <BrandSection />
        <StatsAboutSection />
        <ValuesSection />
        <ReputationSection />
        <ContactSection />
      </main>
      <Footer />
    </>
  );
}